import React, { useState, useEffect } from "react";
import API from "../../services/api";
import { useNavigate } from "react-router-dom";
import { t } from "@lingui/macro";

const PromotionForm = ({ onSave, onCancel }) => {
  const navigate = useNavigate();

  const [prices, setPrices] = useState([]);
  const [promotionType, setPromotionType] = useState("");
  const [selectedPrices, setSelectedPrices] = useState([]);

  const [discountValue, setDiscountValue] = useState("");
  const [freeItems, setFreeItems] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startDisplayDate, setStartDisplayDate] = useState("");
  const [endDisplayDate, setEndDisplayDate] = useState("");

  const [isGlobal, setIsGlobal] = useState(false);

  // Opisy dwujęzyczne
  const [descriptionEn, setDescriptionEn] = useState("");
  const [descriptionEs, setDescriptionEs] = useState("");

  const [image, setImage] = useState(null);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await API.get("/api/prices");
        setPrices(response.data);
      } catch (error) {
        console.error("Error fetching prices:", error);
      }
    };
    fetchPrices();
  }, []);

  const handlePriceSelection = (priceId) => {
    setSelectedPrices((prev) =>
      prev.includes(priceId)
        ? prev.filter((id) => id !== priceId)
        : [...prev, priceId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!descriptionEn.trim() || !descriptionEs.trim()) {
      setErrorMessage(t`Both English and Spanish descriptions are required.`);
      return;
    }

    if (!promotionType) {
      setErrorMessage(t`Promotion type is required.`);
      return;
    }

    if (selectedPrices.length === 0 && !isGlobal) {
      setErrorMessage(t`Select at least one price or mark promotion as global.`);
      return;
    }

    const formData = new FormData();

    formData.append("type", promotionType);

    if (promotionType === "DISCOUNT_PERCENT" || promotionType === "DISCOUNT_AMOUNT") {
      formData.append("discountValue", discountValue);
    }

    if (promotionType === "FREE_ITEM") {
      formData.append("minQuantityForFreeItem", freeItems);
    }

    formData.append("priceIds", selectedPrices.join(","));
    formData.append("startDate", startDate);
    formData.append("endDate", endDate);
    formData.append("startDisplayDate", startDisplayDate);
    formData.append("endDisplayDate", endDisplayDate);
    formData.append("isGlobal", isGlobal);

    formData.append("descriptionEn", descriptionEn);
    formData.append("descriptionEs", descriptionEs);

    if (image) {
      formData.append("image", image);
    }

    try {
      const response = await API.post("/api/promotions", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSuccessMessage(t`Promotion created successfully!`);
      setErrorMessage("");

      setTimeout(() => {
        onSave(response.data);
        navigate("/dashboard");
      }, 2000);
    } catch (err) {
      console.error(err);
      setErrorMessage(t`Error creating promotion.`);
      setSuccessMessage("");
    }
  };

  // 🔥 FILTROWANIE I GRUPOWANIE CEN
  const filteredPrices = prices.filter(
    (price) =>
      (promotionType !== "FREE_ITEM" || price.type !== "Room")
  );

  const ticketPrices = filteredPrices.filter((p) => p.type === "Ticket");
  const roomPrices = filteredPrices.filter((p) => p.type === "Room");

  return (
    <form onSubmit={handleSubmit} className="form-container">
      <h2>{t`Create Promotion`}</h2>

      {successMessage && <div className="success-message">{successMessage}</div>}
      {errorMessage && <div className="error-message">{errorMessage}</div>}

      {/* TYPE */}
      <div>
        <label>{t`Promotion Type`}</label>
        <select value={promotionType} onChange={(e) => setPromotionType(e.target.value)}>
          <option value="">{t`Select Type`}</option>
          <option value="DISCOUNT_PERCENT">{t`Percentage Discount`}</option>
          <option value="DISCOUNT_AMOUNT">{t`Amount Discount`}</option>
          <option value="FREE_ITEM">{t`Free Item`}</option>
        </select>
      </div>

      {/* DISCOUNT / FREE ITEM */}
      {promotionType === "FREE_ITEM" && (
        <div>
          <label>{t`Minimum quantity for free item`}</label>
          <input
            type="number"
            value={freeItems}
            onChange={(e) => setFreeItems(e.target.value)}
            required
          />
        </div>
      )}

      {(promotionType === "DISCOUNT_PERCENT" || promotionType === "DISCOUNT_AMOUNT") && (
        <div>
          <label>{t`Discount value`}</label>
          <input
            type="number"
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            required
          />
        </div>
      )}

      {/* PRICES */}
      <div>
        <h3>{t`Select Prices`}</h3>

        {ticketPrices.length > 0 && (
          <>
            <h4>{t`Tickets`}</h4>
            {ticketPrices.map((price) => (
              <div key={price.id}>
                <input
                  type="checkbox"
                  id={`price-${price.id}`}
                  checked={selectedPrices.includes(price.id)}
                  onChange={() => handlePriceSelection(price.id)}
                />
                <label htmlFor={`price-${price.id}`}>
                  {price.priceCategoryEn} / {price.priceCategoryEs} – {price.price} {price.currency}
                </label>
              </div>
            ))}
          </>
        )}

        {roomPrices.length > 0 && (
          <>
            <h4>{t`Rooms`}</h4>
            {roomPrices.map((price) => (
              <div key={price.id}>
                <input
                  type="checkbox"
                  id={`price-${price.id}`}
                  checked={selectedPrices.includes(price.id)}
                  onChange={() => handlePriceSelection(price.id)}
                />
                <label htmlFor={`price-${price.id}`}>
                  {price.priceCategoryEn} / {price.priceCategoryEs} – {price.price} {price.currency}
                </label>
              </div>
            ))}
          </>
        )}
      </div>

      {/* DATES */}
      <div>
        <label>{t`Start Date`}</label>
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} min={today} required />
      </div>

      <div>
        <label>{t`End Date`}</label>
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} min={startDate} required />
      </div>

      <div>
        <label>{t`Start Display Date`}</label>
        <input type="date" value={startDisplayDate} onChange={(e) => setStartDisplayDate(e.target.value)} min={today} required />
      </div>

      <div>
        <label>{t`End Display Date`}</label>
        <input type="date" value={endDisplayDate} onChange={(e) => setEndDisplayDate(e.target.value)} min={startDisplayDate} required />
      </div>

      {/* GLOBAL */}
      <div>
        <label>
          <input
            type="checkbox"
            checked={isGlobal}
            onChange={(e) => setIsGlobal(e.target.checked)}
            disabled={promotionType === "FREE_ITEM"}
          />
          {t`Global promotion`}
        </label>
      </div>

      {/* DESCRIPTIONS */}
      <div>
        <label>{t`Description (English)`}</label>
        <textarea value={descriptionEn} onChange={(e) => setDescriptionEn(e.target.value)} required />
      </div>

      <div>
        <label>{t`Description (Spanish)`}</label>
        <textarea value={descriptionEs} onChange={(e) => setDescriptionEs(e.target.value)} required />
      </div>

      {/* IMAGE */}
      <div>
        <label>{t`Image (optional)`}</label>
        <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files[0])} />
      </div>

      {/* ACTIONS */}
      <div className="form-actions">
        <button type="button" onClick={onCancel} className="cancel-btn">
          {t`Cancel`}
        </button>
        <button type="submit" className="submit-btn">
          {t`Create Promotion`}
        </button>
      </div>
    </form>
  );
};

export default PromotionForm;
